// frontend/src/components/CRSFormSections/LanguageSection.js
import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography
} from '@mui/material';
import {
  BAD_VALUE,
  yesNoOptions,
  languageTestOptions,
  languageScoreOptions
} from '../../constants/crsFormOptions'; // Adjust path as needed

function LanguageSection({
  formData,
  handleChange,
  handleLanguageScoreChange,
  handleFocus,
  availableSecondLanguageTests,
  showSecondLanguageScores
}) {
  const showFirstLangScores = formData.firstLanguageTestValid === 'B' && formData.firstLanguageTest !== BAD_VALUE;
  const showSecondLangTest = showFirstLangScores && Object.values(formData.firstLanguageScores).every(score => score !== BAD_VALUE);

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Typography variant="h6" sx={{ mt: 2 }}>Language Proficiency</Typography>

      {/* Language Test Validity */}
      <FormControl fullWidth required>
         <InputLabel id="lang-valid-label">5a. Are your language test results less than two years old?</InputLabel>
         <Select
           labelId="lang-valid-label"
           name="firstLanguageTestValid"
           value={formData.firstLanguageTestValid}
           label="5a. Are your language test results less than two years old?"
           onChange={handleChange}
           onFocus={() => handleFocus('firstLanguageTestValid')}
         >
           {yesNoOptions.map(option => (
             <MenuItem key={option.value} value={option.value} disabled={option.value === BAD_VALUE}>
               {option.label}
             </MenuItem>
           ))}
         </Select>
       </FormControl>

     {/* Primary Language Test Selection (Show only if results are valid) */}
     {formData.firstLanguageTestValid === 'B' && (
       <>
         <FormControl fullWidth required>
           <InputLabel id="test-type-label">5b. Which language test did you take for your first official language?</InputLabel>
           <Select
             labelId="test-type-label"
             name="firstLanguageTest"
             value={formData.firstLanguageTest}
             label="5b. Which language test did you take for your first official language?"
             onChange={handleChange}
             onFocus={() => handleFocus('firstLanguageTest')}
           >
             {languageTestOptions.map(option => (
               <MenuItem key={option.value} value={option.value} disabled={option.value === BAD_VALUE}>
                 {option.label}
               </MenuItem>
             ))}
           </Select>
         </FormControl>

         {/* Primary Language Skills Inputs (Show only if test is selected) */}
         {showFirstLangScores && (
           <Box sx={{ border: '1px solid lightgray', p: 2, borderRadius: 1 }}>
             <Typography variant="subtitle1" gutterBottom>Enter Scores for {languageTestOptions.find(t => t.value === formData.firstLanguageTest)?.label}:</Typography>
             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
               {['speaking', 'listening', 'reading', 'writing'].map((skill) => (
                 <FormControl fullWidth key={skill} required>
                   <InputLabel id={`fls-${skill}-label`}>{skill.charAt(0).toUpperCase() + skill.slice(1)}</InputLabel>
                   <Select
                     labelId={`fls-${skill}-label`}
                     name={skill}
                     value={formData.firstLanguageScores[skill]}
                     label={skill.charAt(0).toUpperCase() + skill.slice(1)}
                     onChange={(e) => handleLanguageScoreChange('firstLanguageScores', skill, e.target.value)}
                     onFocus={() => handleFocus(`firstLanguageScores.${skill}`)}
                   >
                     {(languageScoreOptions[formData.firstLanguageTest]?.[skill] || []).map(option => (
                       <MenuItem key={option.value} value={option.value} disabled={option.value === BAD_VALUE}>
                         {option.label}
                       </MenuItem>
                     ))}
                   </Select>
                 </FormControl>
               ))}
             </Box>
           </Box>
         )}

         {/* Second Language Test Selection (Show only if first test scores entered) */}
         {showSecondLangTest && (
           <>
             <FormControl fullWidth required>
               <InputLabel id="second-test-type-label">5c. Which language test did you take for your second official language?</InputLabel>
               <Select
                 labelId="second-test-type-label"
                 name="secondLanguageTest"
                 value={formData.secondLanguageTest}
                 label="5c. Which language test did you take for your second official language?"
                 onChange={handleChange}
                 onFocus={() => handleFocus('secondLanguageTest')}
               >
                 {/* Add "Select..." option manually */}
                 <MenuItem value={BAD_VALUE} disabled>Select...</MenuItem>
                 {availableSecondLanguageTests.map(option => (
                   <MenuItem key={option.value} value={option.value}>
                     {option.label}
                   </MenuItem>
                 ))}
               </Select>
             </FormControl>

             {/* Second Language Skills Inputs (Show only if a second test is selected and it's not 'Not Applicable') */}
             {showSecondLanguageScores && (
               <Box sx={{ border: '1px solid lightgray', p: 2, borderRadius: 1 }}>
                 <Typography variant="subtitle1" gutterBottom>Enter Scores for {languageTestOptions.find(t => t.value === formData.secondLanguageTest)?.label}:</Typography>
                 <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                   {['speaking', 'listening', 'reading', 'writing'].map((skill) => (
                     <FormControl fullWidth key={skill} required>
                       <InputLabel id={`sls-${skill}-label`}>{skill.charAt(0).toUpperCase() + skill.slice(1)}</InputLabel>
                       <Select
                         labelId={`sls-${skill}-label`}
                         name={skill}
                         value={formData.secondLanguageScores[skill]}
                         label={skill.charAt(0).toUpperCase() + skill.slice(1)}
                         onChange={(e) => handleLanguageScoreChange('secondLanguageScores', skill, e.target.value)}
                         onFocus={() => handleFocus(`secondLanguageScores.${skill}`)}
                       >
                         {(languageScoreOptions[formData.secondLanguageTest]?.[skill] || []).map(option => (
                           <MenuItem key={option.value} value={option.value} disabled={option.value === BAD_VALUE}>
                             {option.label}
                           </MenuItem>
                         ))}
                       </Select>
                     </FormControl>
                   ))}
                 </Box>
               </Box>
             )}
           </>
         )}
       </>
     )}
    </Box>
  );
}

export default LanguageSection;